import { Cancel as CancelIcon, Search as SearchIcon } from '@mui/icons-material';
import { CircularProgress, Stack } from '@mui/material';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { debounce, find, isEmpty, isString, isUndefined, map, pick, some, trim } from 'lodash';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { dropIncompleteSyntheticMacros } from 'new/utils/syntheticSearchFilters';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  VFC,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  clearResourceSuggestions,
  getAllReusableTransformsSearchSuggestions,
  getAllSchemaSpecsSearchSuggestions,
  getProjectsSearchSuggestions,
  loadDataCredentialsSearchSuggestions,
  loadDataMapsSearchSuggestions,
  loadDataSetSearchSuggestions,
  loadDataSinkSearchSuggestions,
  loadDataSourceSearchSuggestions,
  loadPipelinesSearchSuggestions,
  setCurrentPage,
  setCurrentPath,
  updateSearchFilters,
} from '../../actions';
import {
  GET_ALL_API_KEYS_REQUEST,
  GET_ALL_SERVICE_KEYS_REQUEST,
  GET_AVAILABLE_DATASETS_REQUEST,
  GET_CODE_CONTAINER_LISTING_REQUEST,
  GET_CODE_CONTAINERS_REQUEST,
  GET_CREDENTIALS_REQUEST,
  GET_DATA_MAPS_REQUEST,
  GET_DATA_SETS_REQUEST,
  GET_DATA_SOURCES_REQUEST,
  GET_PIPELINES_REQUEST,
  GET_PROJECTS_REQUEST,
  GET_SCHEMA_SPECS_REQUEST,
  GET_SINK_REQUEST,
  GET_AI_COMPATIBLE_NEXSETS_REQUEST,
  SEARCH_API_KEYS_REQUEST,
  SEARCH_AVAILABLE_DATASETS_REQUEST,
  SEARCH_CODE_CONTAINERS_REQUEST,
  SEARCH_CREDENTIALS_REQUEST,
  SEARCH_DATA_MAPS_REQUEST,
  SEARCH_DATA_SETS_REQUEST,
  SEARCH_DATA_SOURCES_REQUEST,
  SEARCH_PIPELINES_REQUEST,
  SEARCH_PROJECTS_REQUEST,
  SEARCH_SCHEMA_SPECS_REQUEST,
  SEARCH_SINK_REQUEST,
} from '../../constants/actionTypes';
import {
  DefaultFiltersByView,
  SearchFilterViews,
  SearchFilterViewsType,
} from '../../constants/visibilityFilters';
import { useMacroOptionsContext } from '../../contexts/MacroOptionsContext';
import AdvancedSearchIcon from './AdvancedSearchIcon';
import { MacroAutocomplete } from './MacroAutocomplete';
import { MacroOption } from './MacroAutocomplete.data';
import { MacroIcon } from './MacroIcon';
import { MacroInputApi } from './MacroInput';
import { MacroOptions } from './MacroOptions';
import './SearchBar.scss';
import { segmentsToString } from './tools/segmentsToString';
import { AvailableFilters } from './types/AvailableFilters';
import { ComplexValue } from './types/ComplexValue';
import { Macro } from './types/Macro';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  AccessLevelControl,
  AccessLevelControlProps,
} from 'new/components/AccessLevelControl/AccessLevelControl';

type Props = {
  view: SearchFilterViewsType;
  placeholder?: string;
  requestTypes: string[];
  allAvailableFilters: AvailableFilters;
  availableFilterTypes: string[];
  filters: any[];
  resourceSuggestions;
  isLoading: boolean;
  isClientSideSearch?: boolean;
  inModal?: boolean;
  getSourceMeta;
  onResourceSelected;
  accessLevelControlProps: AccessLevelControlProps;
  // REDUX
  setCurrentPage: typeof setCurrentPage;
  clearResourceSuggestions: typeof clearResourceSuggestions;
  loadDataSetSearchSuggestions: typeof loadDataSetSearchSuggestions;
  loadDataSourceSearchSuggestions: typeof loadDataSourceSearchSuggestions;
  loadPipelinesSearchSuggestions: typeof loadPipelinesSearchSuggestions;
  onLoadPipelinesSearchSuggestions?: typeof loadPipelinesSearchSuggestions; // if provided, will suppress the default loadPipelinesSearchSuggestions
  getProjectsSearchSuggestions: typeof getProjectsSearchSuggestions;
  loadDataSinkSearchSuggestions: typeof loadDataSinkSearchSuggestions;
  loadDataMapsSearchSuggestions: typeof loadDataMapsSearchSuggestions;
  loadDataCredentialsSearchSuggestions: typeof loadDataCredentialsSearchSuggestions;
  getAllSchemaSpecsSearchSuggestions: typeof getAllSchemaSpecsSearchSuggestions;
  getAllReusableTransformsSearchSuggestions: typeof getAllReusableTransformsSearchSuggestions;
  updateSearchFilters: typeof updateSearchFilters;
};

const AUTHOR = 'SearchBar';

const SEARCH_HOTKEY = '/';

const VIEWS_WITH_ACCESS_LEVEL_DISABLED = [
  SearchFilterViews.API_KEYS,
  SearchFilterViews.SERVICE_KEYS,
  SearchFilterViews.AVAILABLE_DATA_SETS,
];

const SearchBar: VFC<Props> = forwardRef(
  (
    {
      view, // to decided actions
      placeholder,
      requestTypes, // to determine if isLoading
      onResourceSelected,
      getSourceMeta,
      // REDUX DATA
      allAvailableFilters,
      availableFilterTypes,
      filters,
      resourceSuggestions,
      isClientSideSearch = false,
      inModal = false,
      isLoading,
      accessLevelControlProps = {},
      // REDUX callbacks
      setCurrentPage,
      clearResourceSuggestions,
      loadDataSetSearchSuggestions,
      loadDataSourceSearchSuggestions,
      loadPipelinesSearchSuggestions,
      onLoadPipelinesSearchSuggestions,
      getProjectsSearchSuggestions,
      loadDataSinkSearchSuggestions,
      loadDataMapsSearchSuggestions,
      loadDataCredentialsSearchSuggestions,
      getAllSchemaSpecsSearchSuggestions,
      getAllReusableTransformsSearchSuggestions,
      updateSearchFilters,
    },
    ref,
  ) => {
    const history = useHistory();
    const { projectId } = useParams() as any;
    const availableFilters = useMemo(
      () => pick(allAvailableFilters, availableFilterTypes),
      [allAvailableFilters, availableFilterTypes],
    );
    const { update, isOpen, author, segments } = useMacroOptionsContext();
    const inputRef = useRef<MacroInputApi>();
    const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

    useImperativeHandle(ref, () => inputRef.current);
    const { current: trimmedDefaultValue } = useRef(
      trim(segmentsToString(DefaultFiltersByView[view])),
    );
    const filtersAsString = segmentsToString(filters);
    const segmentsAsString = segmentsToString(segments || []);
    const shouldSyncListingPageInUrl = !isClientSideSearch && !inModal;

    useEffect(() => {
      window.addEventListener('keydown', _onKeyDown);

      return () => {
        window.removeEventListener('keydown', _onKeyDown);
      };
    }, []);

    useEffect(() => {
      const inputApi = inputRef.current;
      if (!inputApi || !author) return;

      if (author !== AUTHOR) {
        inputApi.setSegments(segments);
        _showAllSearchResults(segments || []);
      }
    }, [segments, author]);

    useDidUpdateEffect(() => {
      if (!isOpen && !isUndefined(segments) && trim(segmentsAsString) && !inModal) {
        _getResourceSuggestions(segments as ComplexValue['segments']);
      }
    }, [segmentsAsString]);

    useEffect(() => {
      const inputApi = inputRef.current;
      if (!inputApi) return;

      update((current) => ({
        ...current,
        author: AUTHOR,
        segments: inputApi.getSegments(),
      }));
    }, [filtersAsString]);

    const _onChange = (value: ComplexValue) => {
      update((current) => ({
        ...current,
        author: AUTHOR,
        segments: value.segments,
      }));
    };

    const _onSelect = ({ key, isResourceMatch }: MacroOption) => {
      if (key === 'show-all-results') {
        clearResourceSuggestions();
        _showAllSearchResults(inputRef.current.getSegments());
      } else if (isResourceMatch) {
        onResourceSelected?.(key);
      }
    };

    const _onMenuClose = (value: ComplexValue) => {
      _showAllSearchResults(value.segments);
    };

    const _onClickToggle = (force?: boolean) => {
      update((current) => ({
        ...current,
        isOpen: force ?? !current.isOpen,
      }));
    };

    const _onClear = () => {
      const segments = DefaultFiltersByView[view];
      inputRef.current?.setSegments(segments);
      _persistSearchQuery(segments);
      update((current) => ({
        ...current,
        author: AUTHOR,
        segments: segments,
        isOpen: false,
      }));
    };

    const _showAllSearchResults = (segments: (string | Macro)[]) => {
      // if some of the default filters are not present, add them
      const missingDefaultFilters = DefaultFiltersByView[view].filter(
        (defaultFilter) =>
          !find(segments, (segment) => {
            if (isString(segment)) {
              return true;
            }

            const availableValues = map(
              allAvailableFilters[segment.token].availableValues,
              'value',
            );

            return (
              (segment.token === defaultFilter.token && isEmpty(availableValues)) ||
              availableValues.includes(segment.value)
            );
          }),
      );

      if (!isEmpty(missingDefaultFilters)) {
        inputRef.current.setSegments([...missingDefaultFilters, ...segments]);

        // return to avoid calling updateSearchFilters twice because updating
        // segments will trigger _showAllSearchResults again
        return;
      }

      if (areSegmentsDifferent(filters, segments)) {
        setCurrentPage(1);
        _persistSearchQuery(segments);
      }
    };

    const extractURLSearchParams = useCallback(() => {
      const browserLocation = history.location;
      const { pathname, search: existingQueryString } = browserLocation;
      const urlSearchParams = new URLSearchParams(existingQueryString);
      return { pathname, urlSearchParams };
    }, [history]);

    const updateCurrentPath = (path) => dispatch(setCurrentPath(path));

    const _persistSearchQuery = (segments: (string | Macro)[]) => {
      if (shouldSyncListingPageInUrl) {
        const trimmedSearchFromSegments = trim(segmentsToString(segments));
        const { urlSearchParams, pathname } = extractURLSearchParams();

        urlSearchParams.delete('page');
        urlSearchParams.set('search', trimmedSearchFromSegments);

        const serializedQuery = urlSearchParams.toString();
        history.push({ pathname, search: serializedQuery });
        updateCurrentPath(`${pathname}?${serializedQuery}`);
      }

      updateSearchFilters(view, segments);
    };

    const _getResourceSuggestions = (typedSegments: ComplexValue['segments']) => {
      clearResourceSuggestions();

      // a half-typed synthetic value (archived:fa) would 400 and mark the key invalid
      const segments = dropIncompleteSyntheticMacros(typedSegments);

      // add new view
      switch (view) {
        case SearchFilterViews.SOURCES:
          loadDataSourceSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.MY_DATA_SETS:
          loadDataSetSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.PROJECTS:
          getProjectsSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.PIPELINES:
        case SearchFilterViews.PROJECT_PIPELINES:
          if (onLoadPipelinesSearchSuggestions) {
            onLoadPipelinesSearchSuggestions(segments, availableFilters, projectId);
          } else {
            loadPipelinesSearchSuggestions(segments, availableFilters, projectId);
          }
          break;

        case SearchFilterViews.SEARCH_PIPELINES:
          loadPipelinesSearchSuggestions(segments, availableFilters, projectId, true);
          break;

        case SearchFilterViews.DATA_SINKS:
          loadDataSinkSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.DATA_MAPS:
          loadDataMapsSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.CREDENTIALS:
          loadDataCredentialsSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.REUSABLE_TRANSFORMS:
          getAllReusableTransformsSearchSuggestions(segments, availableFilters);
          break;

        case SearchFilterViews.SCHEMA_SPECS:
          getAllSchemaSpecsSearchSuggestions(segments, availableFilters);
          break;

        // don't show resource suggestions for API keys because the response is
        // an object which complicates things
        // case SearchFilterViews.API_KEYS:
        //   loadApiKeysSearchSuggestions(segments, availableFilters);
        //   break;
      }
    };

    const _onKeyDown = (event: KeyboardEvent) => {
      if (event.key === SEARCH_HOTKEY && event.target === window.document.body) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    const hideALC = useMemo(() => {
      return VIEWS_WITH_ACCESS_LEVEL_DISABLED.includes(view);
    }, [view]);

    const handleAccessLevelListening = useCallback(() => {
      if (shouldSyncListingPageInUrl) {
        const { urlSearchParams, pathname } = extractURLSearchParams();

        urlSearchParams.set('page', '1');

        const nextQueryString = urlSearchParams.toString();
        history.push({ pathname, search: nextQueryString });
        updateCurrentPath(`${pathname}?${nextQueryString}`);
      }

      setCurrentPage(1);
    }, [dispatch, history, setCurrentPage, shouldSyncListingPageInUrl, extractURLSearchParams]);

    return (
      <Stack
        flex={1}
        spacing={2}
        direction="row"
        alignItems="center"
        aria-label="search-bar-container"
      >
        <MacroAutocomplete
          ref={inputRef}
          isOpen={isOpen ? isOpen : undefined}
          view={view}
          placeholder={placeholder}
          menuInPortal={inModal}
          requestTypes={requestTypes}
          value={filtersAsString}
          availableFilters={availableFilters}
          resourceSuggestions={resourceSuggestions}
          getSourceMeta={getSourceMeta}
          onChange={debounce(_onChange, 500)}
          onSelect={_onSelect}
          onClose={_onMenuClose}
          before={() =>
            isLoading ? (
              <div className="macro-icon on-left">
                <CircularProgress size={20} thickness={4} />
              </div>
            ) : (
              <MacroIcon icon={SearchIcon} align="left" />
            )
          }
          after={() => (
            <>
              <div className="search-bar-hotkey">/</div>
              {trim(inputRef.current?.getValue()) !== trimmedDefaultValue && (
                <MacroIcon icon={CancelIcon} hover onClick={_onClear} />
              )}
              <MacroIcon
                icon={AdvancedSearchIcon}
                align="right"
                hover
                active={isOpen}
                onClick={() => _onClickToggle()}
              />
            </>
          )}
          renderMenu={() => {
            if (!isOpen) return;

            return <MacroOptions availableFilters={availableFilters} />;
          }}
        />

        {hideALC ? null : (
          <AccessLevelControl
            {...accessLevelControlProps}
            onChangeAccessLevelListening={handleAccessLevelListening}
          />
        )}
      </Stack>
    );
  },
);

const mapStateToProps = (state, ownProps) => {
  const {
    entities: {
      sources,
      dataSets,
      flowNodes,
      sinks,
      credentials,
      dataMaps,
      transforms,
      schemaSpecs,
      apiKeys,
      projects,
      serviceKeys,
    },
    app: {
      visibilityFilters: { availableFilters: allAvailableFilters, filters, resourceSuggestions },
    },
    request,
  } = state;
  const { view } = ownProps;
  let resourceSuggestionEntities: Pick<any, any>;
  let requestTypes: string[];

  switch (view) {
    case SearchFilterViews.SOURCES:
      requestTypes = [GET_DATA_SOURCES_REQUEST, SEARCH_DATA_SOURCES_REQUEST];
      resourceSuggestionEntities = pick(sources, resourceSuggestions.items);
      break;

    case SearchFilterViews.MY_DATA_SETS:
      requestTypes = [GET_DATA_SETS_REQUEST, SEARCH_DATA_SETS_REQUEST];
      resourceSuggestionEntities = pick(dataSets, resourceSuggestions.items);
      break;

    case SearchFilterViews.AVAILABLE_DATA_SETS:
      requestTypes = [GET_AVAILABLE_DATASETS_REQUEST, SEARCH_AVAILABLE_DATASETS_REQUEST];
      resourceSuggestionEntities = pick(dataSets, resourceSuggestions.items);
      break;

    case SearchFilterViews.PIPELINES:
    case SearchFilterViews.PROJECT_PIPELINES:
    case SearchFilterViews.SEARCH_PIPELINES:
      requestTypes = [GET_PIPELINES_REQUEST, SEARCH_PIPELINES_REQUEST];
      resourceSuggestionEntities = pick(flowNodes, resourceSuggestions.items);
      break;

    case SearchFilterViews.PROJECTS:
      requestTypes = [GET_PROJECTS_REQUEST, SEARCH_PROJECTS_REQUEST];
      resourceSuggestionEntities = pick(projects, resourceSuggestions.items);
      break;

    case SearchFilterViews.DATA_SINKS:
      requestTypes = [GET_SINK_REQUEST, SEARCH_SINK_REQUEST];
      resourceSuggestionEntities = pick(sinks, resourceSuggestions.items);
      break;

    case SearchFilterViews.DATA_MAPS:
      requestTypes = [GET_DATA_MAPS_REQUEST, SEARCH_DATA_MAPS_REQUEST];
      resourceSuggestionEntities = pick(dataMaps, resourceSuggestions.items);
      break;

    case SearchFilterViews.CREDENTIALS:
      requestTypes = [GET_CREDENTIALS_REQUEST, SEARCH_CREDENTIALS_REQUEST];
      resourceSuggestionEntities = pick(credentials, resourceSuggestions.items);
      break;

    case SearchFilterViews.REUSABLE_TRANSFORMS:
      requestTypes = [
        GET_CODE_CONTAINER_LISTING_REQUEST,
        GET_CODE_CONTAINERS_REQUEST,
        SEARCH_CODE_CONTAINERS_REQUEST,
      ];
      resourceSuggestionEntities = pick(transforms, resourceSuggestions.items);
      break;

    case SearchFilterViews.SCHEMA_SPECS:
      requestTypes = [GET_SCHEMA_SPECS_REQUEST, SEARCH_SCHEMA_SPECS_REQUEST];
      resourceSuggestionEntities = pick(schemaSpecs, resourceSuggestions.items);
      break;

    case SearchFilterViews.API_KEYS:
      requestTypes = [GET_ALL_API_KEYS_REQUEST, SEARCH_API_KEYS_REQUEST];
      resourceSuggestionEntities = pick(apiKeys, resourceSuggestions.items);
      break;

    case SearchFilterViews.SERVICE_KEYS:
      requestTypes = [GET_ALL_SERVICE_KEYS_REQUEST];
      resourceSuggestionEntities = pick(serviceKeys, resourceSuggestions.items);
      break;

    case SearchFilterViews.AI_COMPATIBLE_NEXSETS:
      requestTypes = [GET_AI_COMPATIBLE_NEXSETS_REQUEST];
      resourceSuggestionEntities = pick(dataSets, resourceSuggestions.items);
      break;

    default:
      requestTypes = [];
      resourceSuggestionEntities = [];
  }

  const isLoading = some(requestTypes, (requestType) => request[requestType]);

  return {
    requestTypes,
    allAvailableFilters,
    filters: filters[view],
    resourceSuggestions: {
      ...resourceSuggestions,
      items: resourceSuggestionEntities,
    },
    isLoading,
  };
};

export default connect(
  mapStateToProps,
  {
    clearResourceSuggestions,
    loadDataSetSearchSuggestions,
    loadDataSourceSearchSuggestions,
    loadPipelinesSearchSuggestions,
    getProjectsSearchSuggestions,
    loadDataSinkSearchSuggestions,
    loadDataMapsSearchSuggestions,
    loadDataCredentialsSearchSuggestions,
    getAllReusableTransformsSearchSuggestions,
    getAllSchemaSpecsSearchSuggestions,
    setCurrentPage,
    updateSearchFilters,
  },
  null,
  { forwardRef: true },
)(SearchBar);

SearchBar.displayName = 'SearchBar';
